/**
 * Laptop table columns (design D01 / D05). The fixed columns are SR, Product, Selling price, Stock,
 * Status and actions. Everything else is picked under "Columns" and kept in the same localStorage key
 * the old picker used ("hkp.productList.visibleExtraColumns").
 *
 * Category and Design no. are not columns of their own: they show on the sub-line under the name.
 */
import { PRODUCT_LIST_COLUMN_STORAGE_KEY } from "../Header";

export { PRODUCT_LIST_COLUMN_STORAGE_KEY };

export type TableColumnField =
  | "category"
  | "color"
  | "costPrice"
  | "designNo"
  | "srNo"
  | "nfcTag"
  | "owners"
  | "orgName"
  | "createdAt";

export type PickableColumn = {
  field: TableColumnField;
  label: string;
  /** Column header (absent for the sub-line pieces). */
  header?: string;
  width?: string;
  /** GET /product/products sortBy for a sortable column. */
  sortKey?: string;
  /** Shown on the product's sub-line instead of as a column. */
  subLine?: boolean;
};

/** Picker order = design D05 order, then the older extras the studio could already show. */
export const PICKABLE_COLUMNS: PickableColumn[] = [
  { field: "category", label: "Category", subLine: true },
  { field: "color", label: "Colour", header: "Colour", width: "minmax(110px, 1.2fr)" },
  { field: "costPrice", label: "Cost price", header: "Cost price", width: "124px" },
  { field: "designNo", label: "Design no.", subLine: true },
  { field: "srNo", label: "HKP tag no.", header: "HKP tag no.", width: "minmax(96px, 1fr)", sortKey: "srNo" },
  { field: "nfcTag", label: "NFC tag", header: "NFC tag", width: "minmax(96px, 1fr)", sortKey: "xuid" },
  { field: "owners", label: "Product / design owner", header: "Owner", width: "minmax(140px, 1.3fr)" },
  { field: "orgName", label: "Creator studio", header: "Creator studio", width: "minmax(120px, 1.2fr)", sortKey: "createdByOrganization" },
  { field: "createdAt", label: "Created on", header: "Created on", width: "minmax(120px, 1fr)", sortKey: "createdAt" },
];

export const DEFAULT_TABLE_COLUMNS: TableColumnField[] = ["category", "color", "costPrice", "designNo"];

const KNOWN = new Set<string>(PICKABLE_COLUMNS.map((c) => c.field));

/** Old picker fields → new ones (the two owner columns became one). */
const LEGACY: Record<string, TableColumnField> = {
  color: "color",
  designNo: "designNo",
  orgName: "orgName",
  productOwner: "owners",
  designOwner: "owners",
  srNo: "srNo",
  nfcTag: "nfcTag",
  createdAt: "createdAt",
};

const ordered = (fields: Iterable<string>): TableColumnField[] => {
  const set = new Set(fields);
  return PICKABLE_COLUMNS.map((c) => c.field).filter((f) => set.has(f));
};

/**
 * Stored value → shown fields.
 * - nothing stored: the defaults (Category, Colour, Cost price, Design no.)
 * - `{ v: 2, shown: [...] }`: exactly what the studio picked
 * - an old array of extra columns: the defaults plus those extras (they were picked on purpose)
 */
export function parseTableColumns(raw: string | null): TableColumnField[] {
  if (!raw) return DEFAULT_TABLE_COLUMNS.slice();
  let parsed: any;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return DEFAULT_TABLE_COLUMNS.slice();
  }
  if (parsed && typeof parsed === "object" && !Array.isArray(parsed) && parsed.v === 2 && Array.isArray(parsed.shown)) {
    return ordered(parsed.shown.filter((f: unknown) => typeof f === "string" && KNOWN.has(f)));
  }
  if (Array.isArray(parsed)) {
    const legacy = parsed.filter((f) => typeof f === "string" && LEGACY[f]).map((f) => LEGACY[f]);
    return ordered([...DEFAULT_TABLE_COLUMNS, ...legacy]);
  }
  return DEFAULT_TABLE_COLUMNS.slice();
}

export function serializeTableColumns(shown: TableColumnField[]): string {
  return JSON.stringify({ v: 2, shown: ordered(shown) });
}

export function toggleTableColumn(shown: TableColumnField[], field: TableColumnField): TableColumnField[] {
  return shown.includes(field) ? shown.filter((f) => f !== field) : ordered([...shown, field]);
}

/** Columns drawn between Product and Selling price, and the one after it (cost). */
export function extraTableColumns(shown: TableColumnField[]) {
  const columns = PICKABLE_COLUMNS.filter((c) => !c.subLine && shown.includes(c.field));
  return {
    beforePrice: columns.filter((c) => c.field !== "costPrice"),
    cost: columns.find((c) => c.field === "costPrice") || null,
  };
}
